/** Dash skill for enemies — directional lunge with optional contact damage. */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';
import type { EnemyStamina } from './EnemyStamina';
import { magnitude } from '../../utils/Math';

export interface EnemyDashConfig {
  dashSpeed: number;
  dashDurationMs: number;
  windupMs: number;
  recoveryMs: number;
  cooldownMs: number;
  staminaCost: number;
  /** Contact damage during dash (0 = no damage). */
  contactDamage: number;
  attackType: string;
  /** 'toward' dashes at the player, 'away' repositions backward. */
  dashDirection: 'toward' | 'away';
}

/** Stamina-gated directional dash with optional contact damage on collision. */
export class EnemyDashSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyDashConfig;
  private readonly stamina: EnemyStamina;

  /** Direction vector captured at activation (committed, no tracking). */
  private _dashDirX: number = 0;
  private _dashDirY: number = 0;

  /** Player position must be set before activate() via setTarget(). */
  private _targetX: number = 0;
  private _targetY: number = 0;
  private _originX: number = 0;
  private _originY: number = 0;

  constructor(skillId: string, config: EnemyDashConfig, stamina: EnemyStamina) {
    super();
    this.skillId = skillId;
    this.config = config;
    this.stamina = stamina;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.dashDurationMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected canActivateExtra(): boolean {
    return this.stamina.current >= this.config.staminaCost;
  }

  protected onActivate(): void {
    this.stamina.consume(this.config.staminaCost);
    const dx = this._targetX - this._originX;
    const dy = this._targetY - this._originY;
    const len = magnitude(dx, dy) || 1;
    const sign = this.config.dashDirection === 'away' ? -1 : 1;
    this._dashDirX = (dx / len) * sign;
    this._dashDirY = (dy / len) * sign;
  }

  get skillRange(): number { return this.config.dashSpeed * this.config.dashDurationMs / 1000; }

  /** Set the player target and enemy origin before calling activate(). */
  setTarget(enemyX: number, enemyY: number, playerX: number, playerY: number): void {
    this._originX = enemyX;
    this._originY = enemyY;
    this._targetX = playerX;
    this._targetY = playerY;
  }

  isInDamagePhase(): boolean {
    return this.phase === 'active' && this.config.contactDamage > 0;
  }

  getMovement(): EnemySkillMovement | null {
    if (this.phase !== 'active') return null;
    return {
      vx: this._dashDirX * this.config.dashSpeed,
      vy: this._dashDirY * this.config.dashSpeed,
    };
  }

  getDamageInfo(): EnemySkillDamageInfo | null {
    if (!this.isInDamagePhase()) return null;
    return {
      baseDamage: this.config.contactDamage,
      attackType: this.config.attackType,
      range: 20, // contact distance — must be close to the enemy body
    };
  }
}
